import { Injectable, signal } from '@angular/core';
import { DeviceId, DeviceType } from '../../../../shared/models/device';
import { BacnetPoint } from '../../../../shared/models/point';
import { ModbusPoint } from '../../../../shared/models/modbus';
import { LoRaWANPoint } from '../../../../shared/models/lorawan';
import { TileProcessingItem } from '../models/csv-tile';

export enum BulkModalType {
  None = '',
  Creation = 'CREATION',
  Processing = 'PROCESSING',
  Configuration = 'CONFIGURATION'
}

export interface BulkCreationModalData {
  deviceId?: DeviceId;
  points?: BacnetPoint[] | ModbusPoint[] | LoRaWANPoint[];
  protocol?: DeviceType;
}

export interface BulkProcessingModalData {
  processingItems?: TileProcessingItem[];
  deviceId?: DeviceId;
  isConfigurationMode?: boolean;
  // Preserve creation data for "go back" functionality
  creationData?: BulkCreationModalData;
}

export interface BulkConfigurationModalData {
  // Configuration modal doesn't need any data - user uploads CSV with tile IDs
}

// Discriminated union for type-safe modal state
export type ModalState =
  | { type: BulkModalType.None }
  | { type: BulkModalType.Creation; data: BulkCreationModalData }
  | { type: BulkModalType.Processing; data: BulkProcessingModalData }
  | { type: BulkModalType.Configuration; data: BulkConfigurationModalData };

@Injectable({
  providedIn: 'root'
})
export class BulkModalService {
  // Single signal for type-safe modal state
  modalState = signal<ModalState>({ type: BulkModalType.None });

  openCreationModal(data: BulkCreationModalData): void {
    this.modalState.set({ type: BulkModalType.Creation, data });
  }

  openProcessingModal(data: BulkProcessingModalData): void {
    this.modalState.set({ type: BulkModalType.Processing, data });
  }

  openConfigurationModal(data: BulkConfigurationModalData = {}): void {
    this.modalState.set({ type: BulkModalType.Configuration, data });
  }

  close(): void {
    this.modalState.set({ type: BulkModalType.None });
  }

  // Transition from creation to processing
  transitionToProcessing(items: TileProcessingItem[], deviceId: DeviceId): void {
    // Preserve current creation data for "go back" functionality
    const currentState = this.modalState();
    const creationData = currentState.type === BulkModalType.Creation ? currentState.data : undefined;

    this.modalState.set({
      type: BulkModalType.Processing,
      data: {
        processingItems: items,
        deviceId,
        creationData  // Preserve for go back
      }
    });
  }

  // Go back from processing to creation
  backToCreation(): void {
    const currentState = this.modalState();

    // Get preserved creation data from processing state
    if (currentState.type === BulkModalType.Processing && currentState.data.creationData) {
      this.modalState.set({
        type: BulkModalType.Creation,
        data: currentState.data.creationData
      });
    }
  }

  // Go back from processing to configuration
  // Note: Configuration component gets tile IDs from uploaded CSV, not from parameters
  backToConfiguration(): void {
    this.modalState.set({ type: BulkModalType.Configuration, data: {} });
  }
}
